import type { TargetProbeLegRecord } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Chip, cx, Tooltip } from '../../ui/index.ts';
import { disabledMemberNotes, isGuardTriggerReason, triggerKey } from './legEvidence.ts';
import { guardFaultChips, type GuardFaultChipSpec } from './legFaults.ts';
import { GUARANTEED_CUSTOMERS_STALE_MS, useQuotaCustomers } from '../../api/customerOwnership.ts';
import { LegActions } from './LegActions.tsx';
import { LegLampMark } from './LegLampMark.tsx';
import { LegLocationsLink } from './LegLocationsLink.tsx';
import { legHasLiveDeprecatedConfig, legMemberBreakdown } from './legLocations.ts';
import { legLevelPills, type LevelPillKind } from './levelModel.ts';
import { legAgoText, legNextProbeMs, minutesUntil, type LegSkuArming } from './legModel.ts';
import { physicalLastProbeAt, type LegState } from './legPhysical.ts';
import type { EffectiveRouting, LegRoutingRole } from './legRouting.ts';
import type { PlacedLeg } from './legSkus.ts';
import {
  evidenceLines,
  legImmuneLeft,
  legStateLabel,
  legStateShort,
  roleText,
  rolesSentence,
  type EvidenceTone,
} from './legText.ts';

/**
 * One physical leg as a card-row (#2658): identity and per-region state on
 * the first line, one chip per routing in effect on the second, an evidence
 * callout only when something is off, and a muted meta footer.
 */

const BASE_REGIONS = ['jp', 'cn'];

function memberId(leg: TargetProbeLegRecord): string {
  return leg.members[0]?.target_id ?? `${leg.sku_id}:${leg.upstream_id}:${leg.upstream_channel_id}`;
}

/** 「61 个目标：不接流量 61（全部在已过时配置）」 — where the shared targets sit. */
function memberWhere(leg: TargetProbeLegRecord, t: ReturnType<typeof useT>): string {
  const breakdown = legMemberBreakdown(leg);
  const parts: string[] = [];
  for (const reason of ['global', 'override', 'org_assigned', 'default_fallthrough'] as const) {
    const count = breakdown.byReason.get(reason) ?? 0;
    if (count > 0) parts.push(`${t(`guaranteed.leg.reach.${reason}`)} ${count}`);
  }
  if (breakdown.unreachable > 0) {
    const count = String(breakdown.unreachable);
    parts.push(breakdown.unreachableDeprecated === 0
      ? t('guaranteed.leg.members_where_unreachable', { count })
      : breakdown.unreachableDeprecated === breakdown.unreachable
        ? t('guaranteed.leg.members_where_unreachable_dep_all', { count })
        : t('guaranteed.leg.members_where_unreachable_dep', {
          count,
          deprecated: String(breakdown.unreachableDeprecated),
        }));
  }
  const unknown = breakdown.byReason.get('unknown') ?? 0;
  if (unknown > 0) parts.push(`${t('guaranteed.leg.reach.unknown')} ${unknown}`);
  return t('guaranteed.leg.members_where', { total: String(breakdown.total), parts: parts.join(' · ') });
}

const PILL_TONE: Readonly<Record<LegState, string>> = {
  ok: 'border-ok-line text-ok',
  failing: 'border-warn-line bg-warn-dim/50 text-warn',
  shadow: 'border-warn-line bg-warn-dim/50 text-warn',
  tripped: 'border-bad-line bg-bad-dim/50 text-bad',
  record: 'border-dashed border-bad-line text-paper-mid',
  immune: 'border-signal-deep/50 bg-signal-dim text-signal',
  unprobed: 'border-dashed border-line-strong text-paper-low',
  manual: 'border-line-strong bg-raised text-paper-mid',
};

function RegionPills({ placed, nowMs, testKey }: {
  readonly placed: PlacedLeg;
  readonly nowMs: number;
  readonly testKey: string;
}): JSX.Element {
  const t = useT();
  const { leg } = placed;
  const regions = [...BASE_REGIONS, ...leg.slices.map((entry) => entry.region).filter((region) => !BASE_REGIONS.includes(region))];
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      {regions.map((region) => {
        const entry = leg.slices.find((slice) => slice.region === region);
        const tag = region.toUpperCase();
        if (entry === undefined) {
          return (
            <span
              key={region}
              title={t('guaranteed.legs.region.missing_tip', { region: tag })}
              className="inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-line px-2 text-micro text-paper-low"
              data-testid={`guaranteed-leg-region-${testKey}-${region}`}
              data-state="none"
            >
              <span className="font-mono">{tag}</span>
              <span>—</span>
            </span>
          );
        }
        const state = leg.state === 'manual' ? 'manual' : entry.state;
        const immune = state === 'immune' ? legImmuneLeft({ ...leg, slices: [entry] }, nowMs) : null;
        return (
          <span
            key={region}
            title={t('guaranteed.legs.region.tip', { region: tag, state: legStateLabel(state, immune, t) })}
            className={cx(
              'inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 text-micro',
              PILL_TONE[state],
            )}
            data-testid={`guaranteed-leg-region-${testKey}-${region}`}
            data-state={state}
          >
            <span className="font-mono opacity-80">{tag}</span>
            <span>{legStateShort(state, t)}</span>
            {immune === null ? null : <span className="font-mono">{`${String(immune)}′`}</span>}
          </span>
        );
      })}
    </span>
  );
}

function RoleChip({ role, label, longLabel, t, testId }: {
  readonly role: LegRoutingRole;
  readonly label: string;
  /** Full routing name for the hover title. */
  readonly longLabel: string;
  readonly t: ReturnType<typeof useT>;
  readonly testId: string;
}): JSX.Element {
  const { share } = role;
  const primary = share.role !== 'fallback' && !share.disabled;
  const takeover = role.takeover.length > 0;
  const title = t('guaranteed.legs.role.title', {
    routing: longLabel,
    weight: share.weight === null ? '—' : String(share.weight),
    role: roleText(role, t),
  });
  return (
    <span
      title={title}
      data-testid={testId}
      data-role={share.role}
      data-takeover={takeover ? role.takeover.join(',') : undefined}
      className={cx(
        'inline-flex h-5 max-w-full items-center gap-1.5 whitespace-nowrap rounded border px-1.5 text-micro',
        takeover
          ? 'border-warn-line bg-warn-dim/50 text-warn'
          : primary ? 'border-signal-deep/50 bg-signal-dim text-signal' : 'border-line text-paper-mid',
        share.disabled && 'text-paper-low line-through',
      )}
    >
      <span className="truncate">{label}</span>
      <span aria-hidden="true" className="opacity-60">·</span>
      <span className="font-mono">{roleText(role, t)}</span>
    </span>
  );
}

/** 战争 is the loud one; 警戒 and the cooldown are notes. */
const LEVEL_TONE: Readonly<Record<LevelPillKind, 'bad' | 'warn' | 'neutral'>> = {
  war: 'bad',
  alert: 'warn',
  cooldown: 'neutral',
};

const CALLOUT_TONE: Readonly<Record<LegState, { readonly box: string; readonly tone: EvidenceTone }>> = {
  ok: { box: '', tone: 'plain' },
  tripped: { box: 'border-bad bg-bad-dim/35', tone: 'bad' },
  record: { box: 'border-bad-line bg-well/60', tone: 'plain' },
  shadow: { box: 'border-warn bg-warn-dim/35', tone: 'warn' },
  failing: { box: 'border-warn bg-warn-dim/35', tone: 'warn' },
  immune: { box: 'border-signal bg-signal-dim', tone: 'plain' },
  unprobed: { box: 'border-line-strong bg-well/60', tone: 'plain' },
  manual: { box: 'border-line-strong bg-well/60', tone: 'plain' },
};

const LINE_TONE: Readonly<Record<EvidenceTone, string>> = {
  bad: 'text-bad-bright',
  warn: 'text-paper',
  plain: 'text-paper-mid',
};

export function LegRow({ placed, arming, intervalMinutes, qualityBlindMinutes, label, chipLabel, nowMs }: {
  readonly placed: PlacedLeg;
  readonly arming: LegSkuArming;
  /** SKU probe interval, for the next-probe estimate; null when unknown. */
  readonly intervalMinutes: number | null;
  /** Minutes after a reset in which quality cannot remove the leg (`qualityBlindMinutesAfterReset`). */
  readonly qualityBlindMinutes: number | null;
  /** Long routing label (sentences, titles). */
  readonly label: (routing: EffectiveRouting) => string;
  /** Short routing label for the role chips. */
  readonly chipLabel: (routing: EffectiveRouting) => string;
  readonly nowMs: number;
}): JSX.Element {
  const t = useT();
  const { leg } = placed;
  const head = leg.head;
  const targetId = memberId(head);
  const state = leg.state;
  // Dim unless the leg takes traffic or is a manual removal an operator may add back.
  const reachable = placed.section !== 'dead' && placed.section !== 'overridden';
  const disabledCount = head.members.filter((member) => !member.enabled).length;
  const lastProbe = physicalLastProbeAt(leg);
  const lastCodes = leg.slices
    .filter((entry) => entry.slice.last_probe_at !== null)
    .map((entry) => `${entry.region.toUpperCase()} ${entry.slice.last_status_code === null ? '—' : String(entry.slice.last_status_code)}`)
    .join(' · ');
  const nexts = leg.slices
    .map((entry) => legNextProbeMs(entry.slice, intervalMinutes))
    .filter((value): value is number => value !== null);
  const nextMin = nexts.length === 0 ? null : minutesUntil(new Date(Math.min(...nexts)).toISOString(), nowMs);
  const nextText = !arming.probesOn
    ? t('guaranteed.leg.next_probes_off')
    : nextMin === null
      ? t('guaranteed.leg.never')
      : nextMin === 0
        ? t('guaranteed.leg.next_due')
        : t('guaranteed.leg.minutes_in', { minutes: String(nextMin) });
  const backoff = Math.max(0, ...leg.slices.map((entry) => entry.slice.backoff_level));
  const memberTip = `${memberWhere(head, t)}\n${t('guaranteed.leg.members_tip', { count: String(head.members.length) })}`;
  const sub = `${head.physical_model} · ${head.upstream_channel_id}`;
  const idsTip = t('guaranteed.legs.ids_tip', {
    upstream: head.upstream_id,
    account: head.account_id,
    bucket: head.key_bucket_id,
  });
  const callout = CALLOUT_TONE[state];
  const lines = state === 'ok' ? [] : evidenceLines(leg, arming, nowMs, t);
  const stateLabel = legStateLabel(state, legImmuneLeft(leg, nowMs), t);

  return (
    <div
      className="flex flex-col gap-2 border-b border-line-soft px-4 py-3 transition-colors duration-120 last:border-b-0 hover:bg-raised/40"
      data-testid={`guaranteed-leg-${targetId}`}
      data-lamp={state === 'record' ? 'tripped' : state}
      data-state={state}
      data-section={placed.section}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <LegLampMark state={state} reachable={reachable} size="md" testId={`guaranteed-leg-lamp-${targetId}`} />
        <span className="sr-only">{stateLabel}</span>
        <div className="flex min-w-0 flex-1 items-baseline gap-2.5">
          <span
            className={cx('shrink-0 truncate text-body font-medium', reachable ? 'text-paper' : 'text-paper-mid')}
            title={head.upstream_name}
          >
            {head.upstream_name}
          </span>
          <Tooltip content={<span className="whitespace-pre-line font-mono">{idsTip}</span>}>
            <span
              className="min-w-0 cursor-default truncate font-mono text-label text-paper-low"
              title={`${sub}\n${idsTip}`}
              data-testid={`guaranteed-leg-sub-${targetId}`}
            >
              {sub}
            </span>
          </Tooltip>
          {state !== 'manual' && disabledCount > 0 ? (
            <span
              className="shrink-0 text-micro text-paper-low"
              title={[t('guaranteed.leg.partly_disabled_tip'), disabledMemberNotes(head, t)]
                .filter((part) => part.length > 0).join('\n')}
              data-testid={`guaranteed-leg-partly-disabled-${targetId}`}
            >
              {t('guaranteed.leg.partly_disabled', { disabled: String(disabledCount), total: String(head.members.length) })}
            </span>
          ) : null}
          {placed.section === 'overridden' ? (
            <span className="shrink-0 self-center" title={t('guaranteed.legs.overridden_tip')}>
              <Chip tone="warn" lamp={false} testId={`guaranteed-leg-overridden-${targetId}`}>
                {t('guaranteed.legs.overridden_tag')}
              </Chip>
            </span>
          ) : !reachable ? (
            <span className="shrink-0 self-center" title={t('guaranteed.leg.tag.unreachable_tip')}>
              <Chip tone="neutral" lamp={false} testId={`guaranteed-leg-unreachable-${targetId}`}>
                {t('guaranteed.leg.tag.unreachable')}
              </Chip>
            </span>
          ) : null}
          {legLevelPills(leg.slices, nowMs, t, arming.probesOn).map((pill) => (
            <span key={`${pill.region}:${pill.kind}`} className="shrink-0 self-center" title={pill.tip}>
              <Chip
                tone={LEVEL_TONE[pill.kind]}
                lamp={false}
                testId={`guaranteed-leg-level-${targetId}-${pill.region}-${pill.kind}`}
                data-level={pill.kind}
              >
                {pill.text}
              </Chip>
            </span>
          ))}
          {guardFaultChips(head).map((fault) => (
            <GuardFaultChip key={`${fault.orgId ?? ''}:${fault.faultKind}`} fault={fault} targetId={targetId} />
          ))}
          {placed.section === 'overridden' || !reachable ? null : legHasLiveDeprecatedConfig(head) ? (
            <span className="shrink-0 self-center" title={t('guaranteed.leg.tag.live_deprecated_tip')}>
              <Chip tone="neutral" lamp={false} testId={`guaranteed-leg-live-deprecated-${targetId}`}>
                {t('guaranteed.leg.tag.live_deprecated')}
              </Chip>
            </span>
          ) : null}
        </div>
        <RegionPills placed={placed} nowMs={nowMs} testKey={targetId} />
        {/* Always visible (DESIGN.md §6 bans hover reveals); muted until hovered or focused. */}
        <span className="shrink-0 [&_button:focus-visible]:text-paper [&_button:hover]:text-paper [&_button]:text-paper-low">
          <LegActions
            leg={head}
            state={state}
            targetId={targetId}
            regions={leg.slices}
            qualityBlindMinutes={qualityBlindMinutes}
          />
        </span>
      </div>

      {placed.roles.length === 0 ? null : (
        <div
          className="flex flex-wrap gap-1.5 pl-[18px]"
          title={rolesSentence(placed.roles, label, t)}
          data-testid={`guaranteed-leg-roles-${targetId}`}
        >
          {placed.roles.map((role) => (
            <RoleChip
              key={role.routing.key}
              role={role}
              label={chipLabel(role.routing)}
              longLabel={label(role.routing)}
              t={t}
              testId={`guaranteed-leg-role-${targetId}-${role.routing.key}`}
            />
          ))}
        </div>
      )}

      {lines.length === 0 ? null : (
        <div
          className={cx('ml-[18px] flex flex-col gap-0.5 rounded-sm border-l-2 px-2.5 py-1.5', callout.box)}
          data-testid={`guaranteed-leg-evidence-${targetId}`}
        >
          {lines.map((line) => (
            <div
              key={`${line.tag}:${line.text}`}
              className={cx('flex items-baseline gap-2 text-dense', LINE_TONE[line.tone === 'plain' ? callout.tone : line.tone])}
              title={line.title}
            >
              <span className="w-8 shrink-0 whitespace-nowrap font-mono text-micro text-paper-low">
                {line.tag.length === 0 ? legStateShort(state, t) : line.tag}
              </span>
              <span className="min-w-0">{line.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-[18px] text-micro text-paper-low">
        <span>
          {t('guaranteed.leg.col.last')}
          {' '}
          <span className="text-paper-mid">{lastProbe === null ? t('guaranteed.leg.never') : legAgoText(lastProbe, nowMs, t)}</span>
          {lastCodes.length === 0 ? null : <span className="font-mono">{` · ${lastCodes}`}</span>}
        </span>
        <span aria-hidden="true">·</span>
        <span data-testid={`guaranteed-leg-next-${targetId}`}>
          {t('guaranteed.legs.meta.next')}
          {' '}
          <span className="text-paper-mid">{nextText}</span>
        </span>
        {backoff > 0 ? (
          <>
            <span aria-hidden="true">·</span>
            <span>
              {t('guaranteed.leg.col.backoff')}
              {' '}
              <span className="font-mono text-paper-mid">{backoff}</span>
            </span>
          </>
        ) : null}
        <span aria-hidden="true">·</span>
        <span className="inline-flex items-center gap-1">
          {t('guaranteed.legs.meta.members_pre')}
          <Tooltip content={<span className="whitespace-pre-line">{memberTip}</span>}>
            <span
              className="cursor-default font-mono text-paper-mid underline decoration-line underline-offset-2"
              title={memberTip}
              data-testid={`guaranteed-leg-members-${targetId}`}
            >
              {head.members.length}
            </span>
          </Tooltip>
          {t('guaranteed.legs.meta.members_post')}
          <span className="-my-1.5">
            <LegLocationsLink leg={head} testKey={targetId} />
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * R1-A8: one guard fault chip. A SKU-global silent / broken chip keeps its
 * pre-v2 test id; every other chip is per (kind, customer). An org-scoped
 * chip names its customer: it retires the leg for that customer only.
 */
function GuardFaultChip({ fault, targetId }: { readonly fault: GuardFaultChipSpec; readonly targetId: string }): JSX.Element | null {
  const t = useT();
  // Only an org chip needs the customer list; the query is shared (and cached) across rows.
  const customers = useQuotaCustomers({ staleTime: GUARANTEED_CUSTOMERS_STALE_MS, enabled: fault.orgId !== null });
  const kind = fault.faultKind;
  if (kind !== 'silent' && kind !== 'broken' && !isGuardTriggerReason(kind)) return null;
  const trigger = t(triggerKey(kind));
  const orgName = fault.orgId === null
    ? null
    : customers.data?.customers.find((row) => row.org_id === fault.orgId)?.org_name ?? fault.orgId;
  const label = orgName === null ? trigger : `${trigger} · ${t('guaranteed.leg.trigger.org_scope', { org: orgName })}`;
  const legacyId = fault.orgId === null && (kind === 'silent' || kind === 'broken')
    ? `guaranteed-leg-${kind}-${fault.mode === 'shadow' ? 'shadow-' : ''}${targetId}`
    : null;
  const testId = legacyId ?? `guaranteed-leg-guard-${targetId}-${kind}${fault.orgId === null ? '' : `-org-${fault.orgId}`}`;
  return (
    <span className="shrink-0 self-center">
      <Chip tone={fault.mode === 'act' ? 'bad' : 'warn'} lamp={false} testId={testId}>
        {fault.mode === 'act' ? label : t('guaranteed.leg.trigger.shadow', { trigger: label })}
      </Chip>
    </span>
  );
}
